import { T } from 'react-translator-component';
import { useLanguage, useLanguageActions } from '@hooks/useLanguage';
import LockABI from '@config/abi/TiFiLock.json';
import { minABI } from '@config/TiFi_min_abi';
import { useWeb3React } from '@web3-react/core';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESS, TIFI_ADDRESS, RPC_URL, BSC_SCAN_URL } from '@config/constants';
import { useQuery } from '@tanstack/react-query';
import { getTiFiPrice } from '@features/Stake/api';
import { convertNumber, getErrorMessage, toFixed } from '@utils/tifi';
import ConnectWallet from '@components/widget/connect-wallet';
import Header from '../Home/components/Header';
import Card from '../../components/elements/Card';
import StakingStatCard from '../Stake/components/StakingStatsCard';
import StakeWidget from '../Stake/components/StakeWidget';
import { stackBg } from '../../assets/images';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmptyState from '@components/widget/empty-state';
import { useLock, useLockActions } from './hooks/useGetLockRecords';
import { toast } from 'react-toastify';
import Button from '@components/elements/Button';
import LockAdd from './widgets/LockAdd';
import LockRecords from './widgets/LockRecords';
import { useAlertActions } from '@hooks/useAlert';
import tifiRoutes from '@components/router/tifiRoutes';

const StakeLock = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { account, library } = useWeb3React();
  const { language } = useLanguage();
  const { setLanguage } = useLanguageActions();
  const [totalLocked, setTotalLocked] = useState<string>('0');
  const { data: price } = useQuery(['price-data'], getTiFiPrice);
  const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
  const [query] = useSearchParams();
  const action = query.get('action');
  const { records } = useLock();
  const navigate = useNavigate();
  const { setLockRecords } = useLockActions();
  const { setAlert } = useAlertActions();

  const getLockStatsAndRecords = useCallback(async () => {
    setLoading(true);
    let contract;
    try {
      contract = new ethers.Contract(TIFI_ADDRESS, minABI, library ? library.getSigner()
        : new ethers.providers.JsonRpcProvider(RPC_URL));
      const balance = await contract.balanceOf(CONTRACT_ADDRESS.LOCK_ADDRESS);
      setTotalLocked(ethers.utils.formatUnits(balance));
    } catch (error) {
      console.error('Cannot get lock stats', error);
      toast.error(T(getErrorMessage(error as any)));
    }
    if (account && library) {
      try {
        contract = new ethers.Contract(CONTRACT_ADDRESS.LOCK_ADDRESS, LockABI.abi, library.getSigner());
        const response = await contract.getRecords({ from: account });
        const r = response[0].map((t: any, i: string | number) => {
          return {
            startTs: Number(t) * 1000,
            endTs: Number(response[1][i]) * 1000,
            principal: toFixed(ethers.utils.formatUnits(response[2][i]), 10),
            reflection: toFixed(ethers.utils.formatUnits(response[3][i]), 10),
            reward: toFixed(ethers.utils.formatUnits(response[4][i]), 10),
          };
        });
        setLockRecords(r);
      } catch (error) {
        setLockRecords([]);
        console.error('Cannot get lock record', error);
      }
    }
    setLoading(false);
  }, [account, library, setLockRecords]);

  useEffect(() => {
    setLanguage({ language: language });
    getLockStatsAndRecords();
  }, [setLanguage, language, getLockStatsAndRecords, query]);

  const handleWithdraw = async (startTs: number) => {
    try {
      const contract = new ethers.Contract(CONTRACT_ADDRESS.LOCK_ADDRESS, LockABI.abi, library.getSigner());
      const tx = await contract.withdraw(startTs);
      setAlert({
        message: T('Transaction submitted! Please wait a few seconds for completion.'),
        type: 'notice',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: T('Check transaction on BSCScan'),
        },
      });
      await tx.wait();
      await getLockStatsAndRecords();
      setAlert({
        message: T('Withdraw Success!'),
        type: 'success',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: T('Check Transaction on BSCScan'),
        },
      });
    } catch (error: any) {
      const supplyError = getErrorMessage(error);
      setAlert({
        message: T(supplyError),
        type: 'error',
        url: { link: '', text: '' },
      });
    }
  }

  const stats = useMemo(() => [
    {
      id: 1,
      title: 'TVL in Staking Lock',
      value: '$' + convertNumber(Number(totalLocked) * (price || 0)),
      tooltip: 'The total value locked in US dollars.'
    },
    {
      id: 2,
      title: 'Total TiFi Token Locked',
      value: convertNumber(totalLocked),
      tooltip: 'The total amount of TiFi Token locked in staking lock.'
    },
    {
      id: 3,
      title: 'Maximum APR',
      value: T('15 % + Reflection'),
      tooltip: 'The maximum APR you can get, you need follow the terms of lock to earn.'
    }
  ], [price, totalLocked]);

  return <section className="py-8 relative">
    {openConnectModal && (
      <ConnectWallet modalClose={() => setOpenConnectModal(false)} />
    )}
    <div className="absolute right-0 top-0 -z-30">
      <img src={stackBg} alt="ChatBall" />
    </div>
    <Header title={T('Lock To Earn')}
      descriptionProp={<span className="flex items-center text-white">
        {T('This exclusive program offers high returns (up to 15% of APR) plus reflection rewards for locking TiFi Token with fix period.')}
      </span>}
    />
    <section className="mt-6">
      <Card className="rounded-lg bg-gradient-to-r from-tifi-dark via-tifi-dark to-[#0027374a] bg-opacity-30 backdrop-blur-xl">
        <section className="grid lg:grid-cols-3 grid-cols-1 gap-4">
          {stats.length > 0 &&
            stats.map((st, idx) => (
              <div
                key={st.id}
                className={`${stats.length - 1 !== idx &&
                  'lg:border-r lg:border-r-light-60'
                  } lg:pl-4`}
              >
                <StakingStatCard
                  title={st.title}
                  desc={st.value}
                  loading={loading}
                  tooltip={st.tooltip}
                />
              </div>
            ))}
        </section>
      </Card>
    </section>
    {
      <section className="mt-6 container w-full md:w-[558px] mx-auto">{
        action === 'add' ? <LockAdd /> : <StakeWidget title="Your Earning Records">
          <>
            {
              account ? (<>
                {records && records?.length > 0 ? <div className="mb-5">
                  <LockRecords onWithdraw={handleWithdraw} />
                </div>
                  : <EmptyState label={T('No lock record found')} />
                }</>) : <EmptyState
                label={'Connect to a wallet to view your earning records.'}
              />}
            {
              account ? (
                <div>
                  <Button
                    className="bg-primary text-white"
                    label={'Start Earning!'}
                    onClick={() => navigate(`${tifiRoutes.STAKE_LOCK}?action=add`)}
                  />
                </div>
              ) : (
                <Button
                  className="bg-primary text-white"
                  label={'Connect Wallet'}
                  onClick={() => setOpenConnectModal(true)}
                  loading={loading}
                />
              )}
          </>
        </StakeWidget>
      }</section>}
  </section>;
};
export default StakeLock;